import React, { useContext, useState } from "react";
import { t } from "ttag";

import type { ImageChooserBlockOrNull } from "@reactivated";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import type { Favorite } from "../../favorites";
import { FavoritesContext, isFavorite } from "../../favorites";
import { AddedToFavoritesModal } from "../AddedToFavoritesModal";
import { Clickable } from "../Clickables";
import { toastQueue } from "../FavoritesToastRegion";
import { Svg } from "../Svg";

import styles from "./index.module.scss";

interface FavoriteToggleButtonProps {
    product: Favorite;
    image?: ImageChooserBlockOrNull;
    className?: string;
    svgClassName?: string;
    textVisible?: boolean;
    removeOnly?: boolean;
}

export const FavoriteToggleButton = ({
    product,
    image,
    className,
    svgClassName,
    textVisible = false,
    removeOnly = false,
}: FavoriteToggleButtonProps) => {
    const favs = useContext(FavoritesContext);
    const isFav = isFavorite(favs.items, product);
    const [modalOpen, setModalOpen] = useState(false);

    if (removeOnly && !isFav) return null;

    const onToggle = () => {
        if (isFav) {
            favs.remove(product);
            toastQueue.add(
                { title: t`Removed from Favorites` },
                { timeout: 5000 },
            );
        } else {
            favs.add(product);
            setModalOpen(true);
        }
    };

    const label = isFav ? t`Remove from Favorites` : t`Add to Favorites`;

    return (
        <>
            <Clickable
                className={className}
                onPress={onToggle}
                data-is-favorite={isFav || undefined}
            >
                <Svg
                    name="heart"
                    className={concatClassNames([styles.heart, svgClassName])}
                    visuallyHiddenText={textVisible ? null : label}
                    aria-hidden={textVisible || undefined}
                />
                {textVisible ? <span>{label}</span> : null}
            </Clickable>
            <AddedToFavoritesModal
                isOpen={modalOpen}
                onOpenChange={setModalOpen}
                product={product}
                image={image}
            />
        </>
    );
};
